/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { ExclamationCircleFilled } from '@ant-design/icons';
import { Form, Modal } from 'antd';
import { useState } from 'react';

import { ApiError, NodeConfig } from '@/client';
import { useMessage } from '@/hooks/message';
import { useUploadConfigRevisionMutation } from '@/hooks/mutation/useUploadConfigRevisionMutation';
import { useUploadNodeConfigMutation } from '@/hooks/mutation/useUploadNodeConfigMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Alert } from '@/primitives/Alert';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { NodeConfigUploadForm, NodeConfigUploadFormFields } from './NodeConfigUploadForm';

export type UploadNodeConfigModalProps = {
  open: boolean;
  onCancel: () => void;
  mode: 'new' | 'revision';
  preSelectedConfig?: NodeConfig;
};

function extractApiErrorMessages(error: unknown): string[] {
  if (error instanceof ApiError) {
    const body = error.body as { message?: string; errors?: Array<{ message?: string }> };
    return body.errors?.map((err) => err.message).filter((msg): msg is string => Boolean(msg)) ?? [];
  }
  return [];
}

export function UploadNodeConfigModal({ open, onCancel, mode, preSelectedConfig }: UploadNodeConfigModalProps) {
  const [form] = Form.useForm<NodeConfigUploadFormFields>();
  const [isUploading, setIsUploading] = useState(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [message] = useMessage();
  const { t } = useMimicTranslation('nodeConfigurations');
  const selectedSubtenantID = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const handleApiError = (error: unknown) => {
    const errorMessages = extractApiErrorMessages(error);
    if (errorMessages.length > 0) {
      setApiErrors(errorMessages);
    }
  };

  const handleMutationStart = () => {
    setIsUploading(true);
    setApiErrors([]);
  };

  const { mutate: uploadNodeConfig } = useUploadNodeConfigMutation({
    onMutate: handleMutationStart,
    onSuccess: () => {
      message.success(t('upload.modal.configurationUploadedSuccessfully'));
      handleClose();
    },
    onError: handleApiError,
    onSettled: () => setIsUploading(false),
  });

  const { mutate: uploadConfigRevision } = useUploadConfigRevisionMutation({
    onMutate: handleMutationStart,
    onSuccess: () => {
      message.success(t('upload.modal.configurationRevisionUploadedSuccessfully'));
      handleClose();
    },
    onError: handleApiError,
    onSettled: () => setIsUploading(false),
  });

  const handleClose = () => {
    form.resetFields();
    setIsUploading(false);
    setApiErrors([]);
    onCancel();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();

      // Ensure we have an uploaded file
      const configFile = formData.configFile?.originFileObj;
      if (!configFile) {
        throw new Error(t('upload.modal.configurationFileRequired'));
      }

      // Read and base64 encode the file content
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const content = reader.result as string;
          // Remove the data URL prefix (data:application/json;base64,) and get just the base64 content
          const base64Content = content.split(',')[1];
          resolve(base64Content);
        };

        reader.onerror = reject;
        reader.readAsDataURL(configFile);
      });

      // Create a blob with the base64 content
      const base64Blob = new Blob([fileContent], { type: 'application/octet-stream' });

      // Auto-generate description based on config schema version
      const description = `Config ${formData.configSchema}`;

      if (mode === 'revision' && preSelectedConfig) {
        // Upload as revision
        uploadConfigRevision({
          configId: preSelectedConfig.id,
          formData: {
            file: base64Blob,
            configSchema: formData.configSchema!,
            description,
          },
        });
      } else if (formData.parentConfigId) {
        // Upload as revision to parent config
        uploadConfigRevision({
          configId: formData.parentConfigId,
          formData: {
            file: base64Blob,
            configSchema: formData.configSchema!,
            description,
          },
        });
      } else {
        // Upload as new config
        uploadNodeConfig({
          formData: {
            file: base64Blob,
            configSchema: formData.configSchema!,
            name: formData.name!,
            os: formData.os!,
            description,
            subtenantId: selectedSubtenantID,
          },
        });
      }
    } catch (error) {
      // Form validation failed
    }
  };

  const getModalTitle = () => {
    let title = t('upload.modal.title');
    if (mode === 'revision') {
      title = t('upload.modal.titleRevision', { configName: preSelectedConfig?.name });
    }
    return <div style={{ padding: '0 0.5em' }}>{title}</div>;
  };

  const getOkButtonText = () => {
    if (mode === 'revision') {
      return t('upload.modal.uploadRevision');
    }
    return t('upload.modal.uploadConfiguration');
  };

  return (
    <Modal
      title={getModalTitle()}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      width={800}
      cancelText={t('upload.modal.cancel')}
      okText={getOkButtonText()}
      okButtonProps={{
        loading: isUploading,
        disabled: isUploading,
      }}
      cancelButtonProps={{
        disabled: isUploading,
      }}
      destroyOnClose
      maskClosable={!isUploading}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
        <NodeConfigUploadForm
          form={form}
          loading={isUploading}
          mode={mode}
          preSelectedConfig={preSelectedConfig}
          hasApiError={apiErrors.length > 0}
          onClearApiErrors={() => setApiErrors([])}
        />
      </div>
      {apiErrors.length > 0 && (
        <div style={{ margin: '0.5rem' }}>
          <Alert
            type="error"
            showIcon
            icon={<ExclamationCircleFilled />}
            message={t('upload.modal.errorBannerFailed')}
            description={
              <>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {apiErrors.map((errorMsg, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={index}>{errorMsg}</li>
                  ))}
                </ul>
                {t('upload.modal.errorBannerCorrection')}
              </>
            }
          />
        </div>
      )}
    </Modal>
  );
}
